/**
 * Guided-tour step — a 4-slide carousel walking the user through Cases,
 * Hearings, Wallet, and AI. Pure presentation; the only side effect is
 * marking the tour complete on "Get started" or "Skip tour".
 */
import { useState } from "react";
import { ChevronLeft, ChevronRight, Briefcase, CalendarClock, Wallet, Sparkles, Rocket } from "lucide-react";

const SLIDES = [
    {
        icon: Briefcase, color: "text-primary",
        title: "Cases — your matter inbox",
        body: "Every matter you handle lives here. Auto-fetch case-details from eCourts, attach documents, log work, and never lose track of an order.",
        cta: "Add a litigation from the Litigations menu when you're ready.",
    },
    {
        icon: CalendarClock, color: "text-emerald-400",
        title: "Hearings — never miss a date",
        body: "Your daily cause-list is synced from eCourts every morning. Get reminders, sync to Google Calendar, and one-tap join VC links.",
        cta: "Toggle SMS / email reminders from Settings → Notifications.",
    },
    {
        icon: Wallet, color: "text-violet-400",
        title: "Wallet — passbook for AI & eCourts spend",
        body: "Top-up once, spend across AI drafts, eCourts API calls, document OCR. Every paisa is tracked in your Wallet passbook with a monthly chart.",
        cta: "Top-up later from Wallet → Add funds.",
    },
    {
        icon: Sparkles, color: "text-amber-400",
        title: "AI — your associate that never sleeps",
        body: "Draft notices, summarise judgments, extract clauses, generate replies. Built on GPT-4o with a fallback tier — your work never stops.",
        cta: "Try it from the AI menu — first ₹50 of credit on us.",
    },
];

export default function TourStep({ onFinish, busy }) {
    const [idx, setIdx] = useState(0);
    const slide = SLIDES[idx];
    const Icon = slide.icon;

    return (
        <div className="max-w-md mx-auto space-y-5" data-testid={`onboarding-tour-slide-${idx}`}>
            <div className="text-center">
                <Icon className={`w-12 h-12 mx-auto ${slide.color}`} strokeWidth={1.25}/>
                <h2 className="text-2xl font-semibold tracking-tight text-foreground mt-3">{slide.title}</h2>
                <p className="text-muted-foreground text-sm mt-2">{slide.body}</p>
                <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-3">{slide.cta}</p>
            </div>

            <div className="flex items-center justify-between gap-3">
                <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
                    data-testid="onboarding-tour-prev"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground disabled:opacity-20">
                    <ChevronLeft className="w-3.5 h-3.5"/> Back
                </button>
                <div className="flex items-center gap-1.5">
                    {SLIDES.map((_, i) => (
                        <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-primary" : "bg-border"}`}/>
                    ))}
                </div>
                {idx < SLIDES.length - 1 ? (
                    <button onClick={() => setIdx(i => i + 1)} data-testid="onboarding-tour-next"
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
                        Next <ChevronRight className="w-3.5 h-3.5"/>
                    </button>
                ) : (
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Last</span>
                )}
            </div>

            <button onClick={onFinish} disabled={busy} data-testid="onboarding-tour-finish"
                className="w-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 px-6 py-2.5 text-xs uppercase tracking-[0.16em] font-medium inline-flex items-center justify-center gap-2">
                <Rocket className="w-3.5 h-3.5"/>
                {busy ? "Loading…" : (idx === SLIDES.length - 1 ? "Open my dashboard" : "Skip tour — open dashboard")}
            </button>
        </div>
    );
}
